(() => {
  const selectors = {
    drawer: '[data-drawer]',
    overlay: '[data-overlay]',
    cartCount: '[data-cart-count]',
    cartDrawer: '[data-cart-drawer]',
    cartItems: '[data-cart-items]',
    cartSubtotal: '[data-cart-subtotal]'
  };

  const overlay = document.querySelector(selectors.overlay);
  const drawers = Array.from(document.querySelectorAll(selectors.drawer));

  const escapeHTML = (value = '') =>
    value
      .toString()
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const formatMoney = (cents) => {
    const amount = (Number(cents || 0) / 100).toFixed(2);
    const format = window.moneyFormat || '${{amount}}';

    return format
      .replace('{{amount}}', amount)
      .replace('{{ amount }}', amount)
      .replace('{{amount_no_decimals}}', Math.round(Number(amount)).toString())
      .replace('{{ amount_no_decimals }}', Math.round(Number(amount)).toString());
  };

  const showOverlay = () => {
    if (!overlay) return;
    overlay.hidden = false;
    requestAnimationFrame(() => overlay.classList.add('is-visible'));
  };

  const hideOverlay = () => {
    if (!overlay) return;
    overlay.classList.remove('is-visible');
    setTimeout(() => {
      if (!document.body.classList.contains('is-locked') && !document.body.classList.contains('facets-open')) {
        overlay.hidden = true;
      }
    }, 180);
  };

  const closeDrawers = () => {
    drawers.forEach((drawer) => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    });
    document.body.classList.remove('is-locked');
    hideOverlay();
  };

  const openDrawer = (name) => {
    const drawer = document.querySelector(`[data-drawer="${name}"]`);
    if (!drawer) return;
    closeDrawers();
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
    showOverlay();

    const focusable = drawer.querySelector('input, button, a, select, textarea');
    if (focusable) focusable.focus({ preventScroll: true });
  };

  document.addEventListener('click', (event) => {
    const openButton = event.target.closest('[data-drawer-open]');
    const closeButton = event.target.closest('[data-drawer-close]');

    if (openButton) {
      event.preventDefault();
      openDrawer(openButton.dataset.drawerOpen);
    }

    if (closeButton) {
      event.preventDefault();
      closeDrawers();
    }
  });

  if (overlay) {
    overlay.addEventListener('click', () => {
      closeDrawers();
      document.body.classList.remove('facets-open');
      hideOverlay();
    });
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeDrawers();
      document.body.classList.remove('facets-open');
      hideOverlay();
    }
  });

  document.querySelectorAll('[data-localization-selector]').forEach((selector) => {
    selector.addEventListener('change', () => selector.closest('form')?.submit());
  });

  const renderCart = (cart) => {
    document.querySelectorAll(selectors.cartCount).forEach((count) => {
      count.textContent = cart.item_count;
    });

    document.querySelectorAll(selectors.cartSubtotal).forEach((subtotal) => {
      subtotal.textContent = formatMoney(cart.total_price);
    });

    const cartItems = document.querySelector(selectors.cartItems);
    if (cartItems) {
      if (cart.item_count === 0) {
        cartItems.innerHTML = `
          <div class="empty-state">
            <p>Your cart is empty.</p>
            <a class="button" href="/collections">Return to shop</a>
          </div>
        `;
      } else {
        cartItems.innerHTML = cart.items
          .map((item, index) => {
            const image = item.image
              ? `<img src="${item.image}" alt="${escapeHTML(item.product_title)}" width="90" height="120" loading="lazy">`
              : '';

            return `
              <div class="cart-item" data-line="${index + 1}">
                <a class="cart-item__image" href="${item.url}">${image}</a>
                <div class="cart-item__details">
                  <a class="cart-item__title" href="${item.url}">${escapeHTML(item.product_title)}</a>
                  <p>${escapeHTML(item.variant_title || '')}</p>
                  <div class="cart-item__meta">
                    <quantity-input class="quantity">
                      <button type="button" data-quantity-minus aria-label="Decrease quantity">-</button>
                      <input type="number" min="0" value="${item.quantity}" data-quantity-input>
                      <button type="button" data-quantity-plus aria-label="Increase quantity">+</button>
                    </quantity-input>
                    <span>${formatMoney(item.final_line_price)}</span>
                  </div>
                </div>
              </div>
            `;
          })
          .join('');
      }
    }

    const shipping = document.querySelector('[data-free-shipping-threshold]');
    if (shipping) {
      const threshold = Number(shipping.dataset.freeShippingThreshold || 0);
      const message = shipping.querySelector('[data-shipping-message]');
      const progress = shipping.querySelector('.progress span');
      const percent = threshold > 0 ? Math.min(100, (cart.total_price / threshold) * 100) : 0;

      if (message) {
        message.textContent =
          threshold > 0 && cart.total_price < threshold
            ? `Spend ${formatMoney(threshold - cart.total_price)} more for free standard shipping.`
            : 'You unlocked free standard shipping.';
      }

      if (progress) progress.style.width = `${percent}%`;
    }
  };

  const fetchCart = async () => {
    const response = await fetch(window.themeRoutes.cart, {
      headers: { Accept: 'application/json' }
    });
    return response.json();
  };

  const updateLineItem = async (line, quantity) => {
    const response = await fetch(window.themeRoutes.cartChange, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ line, quantity })
    });

    if (!response.ok) throw new Error('Unable to update cart');
    renderCart(await response.json());
  };

  document.addEventListener('click', (event) => {
    const minus = event.target.closest('[data-quantity-minus]');
    const plus = event.target.closest('[data-quantity-plus]');
    if (!minus && !plus) return;

    const quantity = event.target.closest('.quantity');
    const input = quantity?.querySelector('[data-quantity-input]');
    if (!input) return;

    const currentValue = Number(input.value || 0);
    const nextValue = plus ? currentValue + 1 : Math.max(Number(input.min || 0), currentValue - 1);
    input.value = nextValue;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-quantity-input]');
    const cartItem = event.target.closest('.cart-drawer .cart-item');
    if (!input || !cartItem) return;

    updateLineItem(Number(cartItem.dataset.line), Number(input.value || 0)).catch(() => {
      window.location.href = '/cart';
    });
  });

  document.querySelectorAll('[data-product-form]').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const submit = form.querySelector('[type="submit"]');
      const originalLabel = submit?.textContent;

      if (submit) {
        submit.disabled = true;
        submit.textContent = 'Adding...';
      }

      try {
        const response = await fetch(window.themeRoutes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });

        if (!response.ok) throw new Error('Unable to add item');

        renderCart(await fetchCart());
        openDrawer('cart');
      } catch (error) {
        form.submit();
      } finally {
        if (submit) {
          submit.disabled = false;
          submit.textContent = originalLabel;
        }
      }
    });
  });

  document.querySelectorAll('[data-slideshow]').forEach((slideshow) => {
    const slides = Array.from(slideshow.querySelectorAll('[data-slide]'));
    const dots = Array.from(slideshow.querySelectorAll('[data-slide-dot]'));
    const prev = slideshow.querySelector('[data-slide-prev]');
    const next = slideshow.querySelector('[data-slide-next]');
    const interval = Number(slideshow.dataset.interval || 5000);
    let activeIndex = 0;
    let timer;

    const activate = (index) => {
      activeIndex = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === activeIndex));
      dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === activeIndex));
    };

    const restart = () => {
      if (slideshow.dataset.autoplay !== 'true' || slides.length < 2) return;
      clearInterval(timer);
      timer = setInterval(() => activate(activeIndex + 1), interval);
    };

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        activate(Number(dot.dataset.slideDot));
        restart();
      });
    });

    prev?.addEventListener('click', () => {
      activate(activeIndex - 1);
      restart();
    });

    next?.addEventListener('click', () => {
      activate(activeIndex + 1);
      restart();
    });

    restart();
  });

  document.querySelectorAll('[data-product-root]').forEach((root) => {
    const variantData = root.querySelector('[data-product-json]');
    const variants = variantData ? JSON.parse(variantData.textContent) : [];
    const variantId = root.querySelector('[data-variant-id]');
    const addButton = root.querySelector('[data-add-to-cart]');
    const sku = root.querySelector('[data-variant-sku]');
    const price = root.querySelector('[data-price]');
    const optionInputs = Array.from(root.querySelectorAll('[data-variant-picker] input[type="radio"]'));

    const updateVariant = () => {
      const selectedOptions = optionInputs
        .filter((input) => input.checked)
        .map((input) => input.value);

      const variant = variants.find((candidate) =>
        candidate.options.every((option, index) => option === selectedOptions[index])
      );

      if (!variant) return;

      if (variantId) variantId.value = variant.id;
      if (sku) sku.textContent = variant.sku || '';

      if (price) {
        price.classList.toggle('price--sale', variant.compare_at_price > variant.price);
        price.innerHTML = `
          <span class="price__current">${formatMoney(variant.price)}</span>
          ${variant.compare_at_price > variant.price ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>` : ''}
        `;
      }

      if (addButton) {
        addButton.disabled = !variant.available;
        addButton.textContent = variant.available ? 'Add to cart' : 'Sold out';
      }

      if (variant.featured_media) {
        const mediaId = variant.featured_media.id.toString();
        root.querySelectorAll('[data-media-id]').forEach((slide) => {
          slide.classList.toggle('is-active', slide.dataset.mediaId === mediaId);
        });
        root.querySelectorAll('[data-media-thumb]').forEach((thumb) => {
          thumb.classList.toggle('is-active', thumb.dataset.mediaThumb === mediaId);
        });
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    optionInputs.forEach((input) => {
      input.addEventListener('change', () => {
        root.querySelectorAll(`input[name="${CSS.escape(input.name)}"]`).forEach((groupInput) => {
          groupInput.closest('.variant-pill')?.classList.toggle('is-selected', groupInput.checked);
        });
        updateVariant();
      });
    });

    root.querySelectorAll('[data-media-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const mediaId = thumb.dataset.mediaThumb;
        root.querySelectorAll('[data-media-id]').forEach((slide) => {
          slide.classList.toggle('is-active', slide.dataset.mediaId === mediaId);
        });
        root.querySelectorAll('[data-media-thumb]').forEach((button) => {
          button.classList.toggle('is-active', button === thumb);
        });
      });
    });
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-open-facets]')) {
      document.body.classList.add('facets-open');
      document.body.classList.add('is-locked');
      showOverlay();
    }

    if (event.target.closest('[data-close-facets]')) {
      document.body.classList.remove('facets-open');
      document.body.classList.remove('is-locked');
      hideOverlay();
    }
  });

  document.querySelectorAll('[data-sort-select]').forEach((select) => {
    select.addEventListener('change', () => select.closest('form')?.submit());
  });
})();
